'use client';

import { AlertTriangle } from 'lucide-react';
import { EmailVerifyInline } from '@/components/auth/EmailVerifyInline';

interface VerifyEmailBannerProps {
  /** Pending email address (auth.users.email — not yet verified). */
  email: string;
}

/**
 * Persistent dashboard banner for Pattern A users who haven't verified their
 * email yet. NOT dismissible — we want this in front of the user every time
 * they navigate until the email gets confirmed. The squatting defense and
 * action gates make this banner the user's only feedback that "you can browse
 * but you can't act yet."
 *
 * The OTP mechanics live in the shared EmailVerifyInline (Plan B wave —
 * the launch ladder and channels dialog mount the same component). Banner
 * mode keeps the full-page reload on success so the dashboard layout
 * re-reads email_confirmed_at server-side and stops rendering the banner.
 *
 * Amber, not red — Pattern A is an inconvenience, not a crisis. The user
 * just landed on the dashboard; we don't want to terrify them.
 */
export function VerifyEmailBanner({ email }: VerifyEmailBannerProps) {
  return (
    <div className="border-b bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-500" />

          <div className="flex-1 text-sm">
            <div className="mb-3 text-amber-900 dark:text-amber-100">
              <span className="font-semibold">Verify your email</span>
              <span className="ml-2 text-amber-800 dark:text-amber-200">
                to publish, book, or invite teammates. We sent a 6-digit code to{' '}
                <span className="font-medium">{email}</span>.
              </span>
            </div>

            <EmailVerifyInline email={email} reloadOnSuccess />
          </div>
        </div>
      </div>
    </div>
  );
}
